import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunkAction } from "../../../redux-store";

export const DISPATCHING_VIEW_RESIZE = "dispatchingViewResize";

interface DispatchingViewResizeState {
  col1: number;
  col3: number;
}

const DEFAULT_STATE: DispatchingViewResizeState = {
  col1: 33,
  col3: 33,
};

const loadInitialState = (): DispatchingViewResizeState => {
  const stored = localStorage.getItem(DISPATCHING_VIEW_RESIZE);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed?.col1 === "number" && typeof parsed?.col3 === "number") {
        return { col1: parsed.col1, col3: parsed.col3 };
      }
    } catch {
      // fall through to defaults
    }
  }
  // No values set on startup -> write the defaults so they persist.
  localStorage.setItem(DISPATCHING_VIEW_RESIZE, JSON.stringify(DEFAULT_STATE));
  return DEFAULT_STATE;
};

const initialState = loadInitialState();

const save = (state: DispatchingViewResizeState) => {
  localStorage.setItem(
    DISPATCHING_VIEW_RESIZE,
    JSON.stringify({ col1: state.col1, col3: state.col3 })
  );
};

const slice = createSlice({
  name: "app/dispatching-view-resize",
  initialState,
  reducers: {
    // These only update state (e.g. live on every drag step). Persisting to
    // localStorage is done separately via persistColumnSizes() so a drag does
    // not trigger hundreds of synchronous writes.
    setCol1: (state, action: PayloadAction<number>) => {
      state.col1 = action.payload;
    },
    setCol3: (state, action: PayloadAction<number>) => {
      state.col3 = action.payload;
    },
  },
});

export const { setCol1, setCol3 } = slice.actions;

// Persist the current column sizes to localStorage. Call this once when a
// resize gesture ends, not on every drag step.
export const persistColumnSizes =
  (): AppThunkAction => (_dispatch, getState) => {
    save(getState().app.dispatchingViewResize);
  };

export default slice.reducer;

// Temporary: expose the setters on the browser window so they can be driven
// from the dev console for manual testing. Uses a dynamic import so the slice
// does not statically depend on redux-store (which would create an import
// cycle and break RootState type inference).
(window as any).setCol1 = (value: number) =>
  import("../../../redux-store").then(({ store }) => {
    store.dispatch(setCol1(value));
    store.dispatch(persistColumnSizes());
  });
(window as any).setCol3 = (value: number) =>
  import("../../../redux-store").then(({ store }) => {
    store.dispatch(setCol3(value));
    store.dispatch(persistColumnSizes());
  });
